import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import Portal from './Portal';
import './GlobalSettingsPopup.css';

interface GlobalSettingsPopupProps {
  onClose: () => void;
  geminiApiKey: string | null;
  onSave: (settings: { tabAutoCloseTimeoutMinutes: number; maxParallelTabs: number; defaultTopProducts: number; geminiApiKey: string | null }) => void;
}

const HelpIcon: React.FC<{ text: string }> = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <span
      className="help-icon ms-2 text-muted"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={{ position: 'relative' }}
    >
      <i className="bi bi-question-circle"></i>
      {showTooltip && (
        <div className="help-tooltip shadow-lg border-0 bg-dark text-white p-2 rounded small" 
             style={{ 
               position: 'absolute', 
               bottom: '125%', 
               left: '50%', 
               transform: 'translateX(-50%)', 
               width: '200px', 
               zIndex: 1100,
               fontSize: '0.75rem',
               pointerEvents: 'none'
             }}>
          {text}
        </div>
      )}
    </span>
  );
};

const GlobalSettingsPopup: React.FC<GlobalSettingsPopupProps> = ({ onClose, geminiApiKey: initialGeminiApiKey, onSave }) => {
  const [tabAutoCloseTimeoutMinutes, setTabAutoCloseTimeoutMinutes] = useState(10);
  const [maxParallelTabs, setMaxParallelTabs] = useState(10);
  const [defaultTopProducts, setDefaultTopProducts] = useState(10);
  const [geminiApiKey, setGeminiApiKey] = useState(initialGeminiApiKey || '');
  const [message, setMessage] = useState('');

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    if (user) {
      const userSettingsRef = doc(db, 'userSettings', user.uid);
      getDoc(userSettingsRef).then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTabAutoCloseTimeoutMinutes(data.tabAutoCloseTimeoutMinutes || 10);
          setMaxParallelTabs(data.maxParallelTabs || 10);
          setDefaultTopProducts(data.defaultTopProducts || 10);
          setGeminiApiKey(data.geminiApiKey || '');
        }
      });
    }
  }, [user]);

  const handleSave = async () => {
    onSave({
      tabAutoCloseTimeoutMinutes,
      maxParallelTabs,
      defaultTopProducts,
      geminiApiKey,
    });
    setMessage('Settings saved successfully!');
    onClose();
  };

  return (
    <Portal>
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 1050 }} tabIndex={-1} onClick={onClose}>
        <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
            <div className="modal-header border-bottom-0 pt-4 px-4 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                  <i className="bi bi-gear text-primary fs-5"></i>
                </div>
                <h5 className="modal-title fw-bold mb-0">Global Settings</h5>
              </div>
              <button type="button" className="btn-close shadow-none" onClick={onClose} aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">
              <p className="text-muted small mb-4">
                Configure how the AI agent researches items. These settings control tab behavior and product results.
              </p>
              
              <div className="mb-4">
                <label className="form-label d-flex align-items-center fw-bold small text-uppercase text-muted">
                  Gemini API Key
                  <a href="https://aistudio.google.com/api-keys" target="_blank" rel="noopener noreferrer" className="ms-2 small text-primary text-decoration-none fw-normal">
                    (Get free key <i className="bi bi-box-arrow-up-right"></i>)
                  </a>
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0"><i className="bi bi-key text-muted"></i></span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0 border-2"
                    value={geminiApiKey}
                    onChange={(e) => setGeminiApiKey(e.target.value)}
                    placeholder="Enter API Key"
                  />
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-6">
                  <label className="form-label fw-bold small text-uppercase text-muted d-flex align-items-center">
                    Timeout (min)
                    <HelpIcon text="Inactive tabs close automatically after this time." />
                  </label>
                  <input
                    type="number"
                    className="form-control border-2"
                    value={tabAutoCloseTimeoutMinutes}
                    onChange={(e) => setTabAutoCloseTimeoutMinutes(parseInt(e.target.value, 10))}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label fw-bold small text-uppercase text-muted d-flex align-items-center">
                    Max Tabs
                    <HelpIcon text="Max simultaneous tabs for processing." />
                  </label>
                  <input
                    type="number"
                    className="form-control border-2"
                    value={maxParallelTabs}
                    onChange={(e) => setMaxParallelTabs(parseInt(e.target.value, 10))}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label fw-bold small text-uppercase text-muted d-flex align-items-center">
                  Display Limit
                  <HelpIcon text="Number of top products to show per item." />
                </label>
                <input
                  type="number"
                  className="form-control border-2"
                  value={defaultTopProducts}
                  onChange={(e) => setDefaultTopProducts(parseInt(e.target.value, 10))}
                />
              </div>

              <div className="d-grid gap-2 mt-2">
                <button className="btn btn-primary btn-lg fw-bold py-2" onClick={handleSave}>Save Changes</button>
                <button className="btn btn-light py-2" onClick={onClose}>Cancel</button>
              </div>
              {message && <p className="text-success text-center mt-3 small mb-0 fw-bold">{message}</p>}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default GlobalSettingsPopup;